import React, { ReactNode } from "react";
import { clamp } from 'lodash';

// TODO: import outside of wombat should be avoided
import { IconProps } from "../../../icons";
import { H2, H4 } from "../Typography";

import useScrollEffect from "../../utils/useScrollEffect";
import { AvatarProps, TailwindSize } from "../Avatar/Avatar";

export type ScreenHeaderProps = {
  /**
   * Page header it describe the principal ressource / action presented in the page
   * @default undefined
   */
  title?: ReactNode;
  /**
   * Helps to give context on what is suppose to happen on this page
   * @default undefined
   */
  description?: ReactNode;
  /**
   * Use an icon if it serve the content of the page or as a reminder of the icon used to represent the main ressource of the page.
   * @default undefined
   */
  icon?: React.FC<IconProps>
  /**
   * Alternate function to render the icon component, a Tailwind size will be passed to the render function.
   */
  renderIcon?: (size: TailwindSize) => React.ReactNode;
  /**
   * Optional avatar that must have a size component, can't be used as the same time as the icon
   * @default undefined
   */
  avatar?: React.FC<AvatarProps>;
  /**
   * Use it to define quick actions, modifying page content
   * @default []
   */
  actions?: React.ReactNode[] | React.ReactNodeArray[];
  /**
   * Use a narrow ScreenHeader layout when no sidebar is used and content is vertical (eg: reports)
   */
  narrow?: boolean;
};

export type ScreenHeaderTitleProps = Pick<ScreenHeaderProps, "title" | "description">

const ScreenHeaderTitle: React.FC<ScreenHeaderTitleProps> = React.memo(({
  title = "",
  description = "",
}) => {
  const scrolled = useScrollEffect();
  const Heading = scrolled ? H4 : H2;

  return (
    <div className={`flex-1 min-w-min transform origin-top-left`}>
      <Heading description={description}>{title}</Heading>
    </div>
  )
});

export type ScreenHeaderIconProps = Pick<ScreenHeaderProps, "icon" | "renderIcon">

const ScreenHeaderIcon: React.FC<ScreenHeaderIconProps> = React.memo(({
  icon: Icon,
  renderIcon
}) => {
  const scrolled = useScrollEffect();

  let renderedIcon;
  if (renderIcon) {
    renderedIcon = (
      <div
       className={`items-center md:flex ${scrolled ? 'mr-4' : `mr-8`}`}
      >
        {renderIcon(scrolled ? 12 : 24)}
      </div>
    )
  } else {
    renderedIcon = (
      <div className={`items-center justify-center flex-shrink-0 hidden rounded-full text-light-0 bg-dark-100 md:flex ${scrolled ? 'w-12 h-12 mr-4' : `w-24 h-24 mr-8`}`}>
        <Icon className={scrolled ? 'w-5 h-5' : 'w-10 h-10'} />
      </div>
    );
  }

  return (
    renderedIcon
  )
});

const ScreenHeader: React.FC<ScreenHeaderProps> = ({
  icon: Icon,
  renderIcon,
  title = "",
  description = "",
  actions = [],
  narrow = false,
}) => {
  return (
    <section className="sticky top-0 z-20 w-full bg-light-0">
      <div
        className={`${
          narrow ? "max-w-4xl" : "max-w-6xl"
        } px-4 py-6 mx-auto md:flex md:items-center md:justify-between md:px-6 lg:px-8`}
      >
        {(Icon || renderIcon) && (
          <ScreenHeaderIcon icon={Icon} renderIcon={renderIcon} />
        )}
        {title && (
          <ScreenHeaderTitle title={title} description={description} />
        )}
        <div className="flex flex-wrap mt-4 space-x-2 md:mt-0 md:ml-4 print:hidden">
          {actions.map((action) => action)}
        </div>
      </div>
    </section>
  );
};

export default React.memo(ScreenHeader);
